import { Paper } from '@mui/material';
import Box from '@mui/material/Box';
import _ from 'lodash';
import { DateTime } from 'luxon';
import qs from 'qs';
import React, { useCallback, useContext, useState } from 'react';
import { View, Views } from 'react-big-calendar';
import { SetURLSearchParams, useSearchParams } from 'react-router-dom';
import { useModuleDrawerContext } from '../../../../contexts/ModuleDrawerContext';
import { useGetAllEvents } from '../../../../services/react-query/events';
import { Event } from '../../../../types/Event';
import { UserConfigurableModule } from '../../../../types/User';
import { Loading } from '../../../../uikit';
import { formatDateRangeForAPI } from '../../../../utility/dateUtils';
import { FilterContext } from '../../../layouts/EventsLayout/EventsLayout';
import Calendar from '../Calender/Calender';
import WeeklyRoleFooter from './WeeklyRoleFooter';

interface ICalendarEvent {
  uuid: string;
  title: string;
  accountName: string;
  start: Date;
  end: Date;
}

// this hack is required since the onNavigate and onView fire in immediate succession when a single day is clicked
// we need to accumulate the changes and send them to the debounced URL update function otherwise they destructively interfere with each other
let accu = {};
const updateSearch = _.debounce((setSearch: SetURLSearchParams) => {
  setSearch((prev) => {
    const prevObj = qs.parse(prev.toString());
    return qs.stringify({ ...prevObj, ..._.cloneDeep(accu) });
  });
  accu = {};
}, 20);

const tooltipAccessor = (event: ICalendarEvent) => `${event.accountName} – ${event.title}`;

export const getDateOrToday = (isoDate: string): Date => (isoDate ? DateTime.fromISO(isoDate).toJSDate() : DateTime.now().toJSDate());

const EventCalendar: React.FC = () => {
  const [searchParams, setSearch] = useSearchParams();
  const searchObj = qs.parse(searchParams.toString());

  const currentView = (searchObj.view as View) || Views.MONTH;
  const anchorDate = getDateOrToday(searchObj.date as string);

  const [dateRange, setDateRange] = useState({
    startDate: DateTime.fromJSDate(anchorDate).minus({ days: 35 }).toISO(),
    endDate: DateTime.fromJSDate(anchorDate).plus({ days: 35 }).toISO(),
  });

  const handleRangeChange = (range: Date[] | { start: Date; end: Date }) => {
    const { formattedStart, formattedEnd } = formatDateRangeForAPI(range);
    setDateRange({ startDate: formattedStart, endDate: formattedEnd });
  };

  const updateDisplay = (updateObj: { date?: string; view?: string }) => {
    accu = { ...accu, ...updateObj };
    updateSearch(setSearch);
  };

  const filters = useContext(FilterContext);
  const { data, isLoading: isGetAllEventsLoading } = useGetAllEvents({
    params: {
      per_page: 0,
      filters: {
        ...filters,
        date_range: [dateRange.startDate, dateRange.endDate],
      },
    },
    enabled: filters !== undefined,
  });

  const { setModuleDrawerState } = useModuleDrawerContext();
  const handleEventSelect = useCallback(
    (event: ICalendarEvent) => {
      setModuleDrawerState({ module: UserConfigurableModule.EVENTS, uuid: event.uuid });
    },
    [setModuleDrawerState]
  );

  if (isGetAllEventsLoading) {
    return <Loading />;
  }

  const events = data?.data || [];

  const calendarEvents: ICalendarEvent[] = _.map(events, (event: Event) => ({
    uuid: event.uuid,
    title: event.name,
    accountName: event.account_name ?? '',
    start: DateTime.fromISO(event.start_at).toJSDate(),
    end: DateTime.fromISO(event.end_at).toJSDate(),
  }));

  return (
    <Box component={Paper} sx={{ p: 1, borderRadius: 4 }}>
      <Box position="relative">
        <Calendar<ICalendarEvent>
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          tooltipAccessor={tooltipAccessor}
          showMultiDayTimes
          selectable
          popup
          onSelectEvent={handleEventSelect}
          onRangeChange={handleRangeChange}
          date={anchorDate}
          view={currentView}
          onView={(view) => updateDisplay({ view })}
          onNavigate={(date) => updateDisplay({ date: DateTime.fromJSDate(date).toISODate()! })}
        />

        {currentView === Views.WEEK && <WeeklyRoleFooter events={events} anchorDate={anchorDate} />}
      </Box>
    </Box>
  );
};

export default EventCalendar;
